import axios from 'axios'
import { useAuthStore } from '@/store/auth'

const API_BASE = process.env.VUE_APP_API_BASE_URL || 'http://localhost:5003/api'
// Origin only (no /api suffix) — used to build URLs for <img> / <v-img> src attributes
export const API_ORIGIN = API_BASE.replace(/\/api.*$/, '')

/**
 * Convert an API-relative path (e.g. "/api/members/3/photo") to a full URL
 * the browser can fetch directly.  Returns null if path is falsy.
 */
export const getFileUrl = (path) => {
  if (!path) return null
  if (path.startsWith('http')) return path
  return `${API_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const authStore = useAuthStore()
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const authStore = useAuthStore()
    const originalRequest = error.config
    
    // Prevent infinite loops on auth endpoints
    if (originalRequest.url && (
      originalRequest.url.includes('/auth/login') ||
      originalRequest.url.includes('/auth/refresh') ||
      originalRequest.url.includes('/auth/register')
    )) {
      return Promise.reject(error)
    }
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      try {
        const success = await authStore.refreshAccessToken()
        if (success) {
          // Update the authorization header for the retry
          originalRequest.headers.Authorization = `Bearer ${authStore.token}`
          return api(originalRequest)
        } else {
          authStore.logout()
          window.location.href = '/auth/login'
          return Promise.reject(error)
        }
      } catch (refreshError) {
        authStore.logout()
        window.location.href = '/auth/login'
        return Promise.reject(refreshError)
      }
    }
    
    return Promise.reject(error)
  }
)

// Export base URL for direct use
export const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:5003/api'

export default api